<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import type { UseProductCustomization } from '../../composables/useProductCustomization'
import type { FeedPreview, FeedSourceDescriptor } from '../../models/product.types'
import { displayColumn } from '../../service/categoryMapping'

import ProductPreview from './ProductPreview.vue'

import { useI18n } from '@/shared/i18n'
import { Alert, Button, Modal, Select, Spinner, type SelectOption } from '@/shared/ui'

const props = defineProps<{
  source: FeedSourceDescriptor
  controller: UseProductCustomization
}>()
const emit = defineEmits<{ close: [] }>()
const { t } = useI18n()
const open = ref(true)
const loading = ref(true)
const options = ref<string[]>([])
const selected = ref<string | null>(null)
const preview = ref<FeedPreview>({ rows: [], columns: [] })
const selectOptions = computed<SelectOption<string>[]>(() =>
  options.value.map((value) => ({ value, label: displayColumn(value) })),
)
const helpKey = computed(() =>
  props.source.platform === 'merchant_center'
    ? 'workspaceDefinitions.product.productId.merchantHelp'
    : 'workspaceDefinitions.product.productId.metaHelp',
)

onMounted(async () => {
  const [optionsResult, previewResult] = await Promise.allSettled([
    props.controller.productIdOptions(props.source),
    props.controller.preview(props.source),
  ])
  if (optionsResult.status === 'fulfilled') options.value = optionsResult.value
  if (previewResult.status === 'fulfilled') preview.value = previewResult.value
  const current = props.controller.productId(props.source.platform)
  selected.value =
    options.value.find((value) => displayColumn(value) === displayColumn(current ?? '')) ?? null
  loading.value = false
})

async function save(): Promise<void> {
  if (await props.controller.saveProductId(props.source, selected.value)) emit('close')
}
</script>

<template>
  <Modal v-model="open" size="xl" @close="emit('close')">
    <template #title>{{ t('workspaceDefinitions.product.title') }}</template>
    <template #body>
      <div class="product-id-modal__body grid gap-md">
        <Spinner v-if="loading" />
        <template v-else>
          <Select
            v-model="selected"
            :options="selectOptions"
            :label="t('workspaceDefinitions.product.productId.title')"
            :placeholder="t('workspaceDefinitions.product.productId.placeholder')"
            searchable
          />
          <p class="product-id-modal__help m-0 text-muted text-[0.75rem]">
            {{ t(helpKey) }}
          </p>
          <ProductPreview
            v-if="preview.rows.length && preview.columns.length"
            :preview="preview"
            v-bind="{ ariaLabel: t('workspaceDefinitions.product.preview') }"
          />
          <Alert v-else tone="info">{{ t('workspaceDefinitions.product.noPreview') }}</Alert>
          <Alert v-if="controller.error.value" tone="danger">
            {{ t('workspaceDefinitions.product.saveError') }}
          </Alert>
        </template>
      </div>
    </template>
    <template #buttons>
      <Button variant="outline" size="sm" @click="emit('close')">
        {{ t('workspaceDefinitions.product.productId.skip') }}
      </Button>
      <Button
        size="sm"
        :loading="controller.pending.value"
        :disabled="!selected"
        data-testid="product-id-save"
        @click="save"
      >
        {{ t('workspaceDefinitions.common.saveChanges') }}
      </Button>
    </template>
  </Modal>
</template>
